/**
 * WindowsBridge
 * Windows WebView2 平台的 Bridge 實作
 */

import { BridgeStrategy } from './BridgeStrategy'
import { BridgeAction } from '../types/bridge.types'
import type { PlatformType } from '../types/platform.types'

export class WindowsBridge extends BridgeStrategy {
  private messageHandler: ((event: MessageEvent) => void) | null = null

  getPlatform(): PlatformType {
    return 'windows'
  }

  async connect(): Promise<void> {
    try {
      this.updateConnectionStatus('connecting')
      this.setupListeners()

      // 測試連接
      const testMessage = this.createMessage('ping', {})

      if (this.win.Windows?.receiveMessage) {
        // 使用 Windows.receiveMessage
        this.win.Windows.receiveMessage(JSON.stringify(testMessage))
      } else if (this.win.chrome?.webview?.postMessage) {
        // 使用 chrome.webview.postMessage
        this.win.chrome.webview.postMessage(testMessage)
      } else if (this.win.asyncBridge) {
        // AsyncBridge 可用，不需要測試
      } else {
        throw new Error('No Windows bridge API available')
      }

      this.updateConnectionStatus('connected')
      this.updateLastActivity()
    } catch (error) {
      this.updateConnectionStatus('error')
      console.error('Failed to connect Windows bridge:', error)
      throw error
    }
  }

  disconnect(): void {
    this.updateConnectionStatus('disconnected')

    // 移除事件監聽器
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler)
      this.messageHandler = null
    }

    // 清理資源
    this.cleanup()
  }

  setupListeners(): void {
    // 設置 PostMessage 監聽器
    if (!this.messageHandler) {
      this.messageHandler = (event: MessageEvent) => {
        try {
          // 驗證來源
          if (
            event.origin !== window.location.origin &&
            event.origin !== 'null'
          ) {
            return
          }

          const data =
            typeof event.data === 'string' ? JSON.parse(event.data) : event.data
          this.handleMessage(data)
        } catch (error) {
          console.error('Error handling message:', error)
        }
      }
      window.addEventListener('message', this.messageHandler)
    }

    // 設置 chrome.webview 監聽器
    if (this.win.chrome?.webview?.addEventListener) {
      this.win.chrome.webview.addEventListener(
        'message',
        (event: { data: unknown }) => {
          try {
            const data =
              typeof event.data === 'string'
                ? JSON.parse(event.data)
                : event.data
            this.handleMessage(data)
          } catch (error) {
            console.error('Error handling webview message:', error)
          }
        }
      )
    }
  }

  /**
   * 發送訊息到 Windows 原生端（實作抽象方法）
   */
  async sendMessage<T>(action: BridgeAction, data: unknown): Promise<T> {
    // 優先使用 AsyncBridge
    const actionName = action as string
    console.log('sendMessage', actionName, data)
    if (this.win.asyncBridge && this.win.asyncBridge[actionName]) {
      const result = await this.callAsyncBridge(actionName, data)

      this.updateLastActivity()
      return this.parseResponse<T>(result)
    }

    throw new Error(`AsyncBridge method ${actionName} not found`)
  }
}
